import {
  parseChipFromCMS,
  parseIconFromCMS,
  parseImageFromCMS,
  parseLinkFromCMS,
} from '@/lib/contentstack-sdk/utils/parseHelpers';
import { IMlCardProps, IMlCardsCMSProps, EMlCardsTypes } from './ml-card.types';
import { ITmBlogCMSProps } from '@/lib/contentstack-sdk/types/pages';
import { EClickableSize, EClickableVariants, EClickableTypes } from '@/components/atoms/at-clickable/at-clickable.types';

// Allow both types of cms card inputs: blog posts + original generic cards
type TMlCardInput = IMlCardsCMSProps | ITmBlogCMSProps;

/**
 * Helper function to map a raw ITmBlogCMSProps entry into the standard IMlCardProps format.
 * This function encapsulates all the logic for image selection, chip creation, and the
 * correctly formatted CTA for blog cards (which was your recent fix).
 */
const mapBlogCard = (post: ITmBlogCMSProps): IMlCardProps => {
  const imageFile =
    post.hero_image?.desktop_image_url ||
    post.hero_image?.mobile_image_url ||
    post.user_images?.image_1;

  const imageUrl = imageFile?.url;
  const imageAlt = imageFile?.description || post.title;
  const hasImage = !!imageUrl;

  return {
    title: post.title,
    body: post.description || post.title,
    variant: (hasImage ? EMlCardsTypes.IMAGE : 'default') as EMlCardsTypes,
    imageProps: hasImage ? {
      src: imageUrl!,
      alt: imageAlt,
    } : undefined,
    chipProps: post.category ? {
      text: post.category,
      variant: 'primary',
    } : undefined,
    theme: 'light',
    headerElement: 'h3',
    alignment: 'left',
    ariaLabel: post.title,
    // This is the fixed, flattened CTA structure
    ctaProps: {
      label: 'Read More',
      href: post.url,
      target: '_self',
      type: EClickableTypes.LINK,
      variant: EClickableVariants.PRIMARY,
      interactLocation: 'ml-card',
      size: EClickableSize.SM,
    },
  } as IMlCardProps;
};

// --- Main Exported Function ---

export const mapCardFromCMS = (card: TMlCardInput): IMlCardProps => {
  // 1. Generic Card Mapping (distinguished by 'header' and 'cta' fields)
  if ('header' in card && 'cta' in card) {
    const {
      cta,
      header,
      header_element,
      body,
      variant,
      theme,
      image,
      alignment,
      chip,
      icon,
      aria_label,
    } = card as IMlCardsCMSProps;

    return {
      title: header,
      body,
      variant,
      theme,
      alignment,
      ariaLabel: aria_label,
      headerElement: header_element?.select,
      iconName: parseIconFromCMS(icon),
      chipProps: parseChipFromCMS(chip),
      // Generic cards use the standard parser for CTA
      ctaProps: parseLinkFromCMS(cta, 'ml-card'),
      imageProps: parseImageFromCMS(image),
    };
  }

  // 2. Blog Post Mapping (since it failed the generic check, it must be a blog post)
  return mapBlogCard(card as ITmBlogCMSProps);
};
